// シフト決定（日表示・カレンダー表示）の割り当てプレビュー
// - 「メイン講師未決定の授業」パネルのセレクトで講師を選ぶと、カレンダーの該当講師の列に
//   その授業のブロック（破線）をプレビュー表示する（保存はしない）
// - 列が無い講師を選んだ場合は列を新しく追加する
// - プレビューが同じ列の既存ブロックと時間帯が被る場合は赤枠 +「重複」で示す
// - メイン講師を選んだ授業は、未決定列のブロックを薄くして「割当予定」と示す
//
// 必要な DOM（_calendar.blade.php）:
//   #lecturer-schedule-calendar[data-height-px][data-role-classes]  カレンダー全体
//   .js-calendar-columns                                             列のコンテナ
//   .js-calendar-column[data-lecturer-id]                           講師の列（未決定列は data-lecturer-id なし）
//   .js-calendar-column-body                                        ブロックを置く時間軸（position: relative）
//   .js-calendar-block[data-lesson-id][data-role][data-start-minutes][data-end-minutes]
//   template#calendar-column-template                               追加する列の雛形
//   #calendar-assignment-panel tr[data-preview-*] select            割り当てパネル

const ROOT_ID = 'lecturer-schedule-calendar'
const PANEL_ID = 'calendar-assignment-panel'
const COLUMN_TEMPLATE_ID = 'calendar-column-template'

/** セレクトの name（online_lesson_settings[ID][役割キー]）から役割へ */
const ROLE_BY_INPUT_KEY = {
    lecturer_user_id: 'main',
    support_lecturer_user_id: 'support',
    observer_lecturer_user_id: 'observer',
}

const ROLE_LABELS = {
    main: 'メイン',
    support: 'サポート',
    observer: 'オブザーバー',
}

const SELECT_NAME_PATTERN = /^online_lesson_settings\[(\d+)\]\[(\w+)\]$/

/**
 * option の表示文字列（例: 〇山田太郎(認定)）から講師名だけを取り出す
 *
 * @param {string} text
 * @returns {string}
 */
const extractLecturerName = (text) => text.replace(/^[〇✕]/, '').replace(/\(認定\)$/, '').trim()

/**
 * 同じ列にある他のブロックと時間帯が被るか
 *
 * @param {HTMLElement} body
 * @param {number} start
 * @param {number} end
 * @param {HTMLElement|null} exclude
 * @returns {boolean}
 */
const isOverlapping = (body, start, end, exclude) => {
    return Array.from(body.querySelectorAll('.js-calendar-block')).some((block) => {
        return block !== exclude
            && Number(block.dataset.startMinutes) < end
            && Number(block.dataset.endMinutes) > start
    })
}

/**
 * カレンダー表示の割り当てプレビューを設定する
 *
 * @returns {void}
 */
export const setUpCalendarPreview = () => {
    const root = document.getElementById(ROOT_ID)
    const panel = document.getElementById(PANEL_ID)
    const template = document.getElementById(COLUMN_TEMPLATE_ID)
    if (!root || !panel || !template) {
        return
    }

    const columnsContainer = root.querySelector('.js-calendar-columns')
    const roleClasses = JSON.parse(root.dataset.roleClasses || '{}')

    /**
     * 講師の列を探し、無ければ雛形から追加する
     *
     * @param {string} lecturerId
     * @param {string} lecturerName
     * @returns {HTMLElement}
     */
    const findOrCreateColumn = (lecturerId, lecturerName) => {
        const existing = root.querySelector(`.js-calendar-column[data-lecturer-id="${lecturerId}"]`)
        if (existing) {
            return existing
        }

        const fragment = template.content.cloneNode(true)
        const column = fragment.querySelector('.js-calendar-column')
        column.dataset.lecturerId = lecturerId
        column.dataset.previewColumn = '1'
        column.querySelector('.js-calendar-column-title').textContent = lecturerName
        column.querySelector('.js-calendar-column-subtitle').textContent = '割り当て予定'
        columnsContainer.appendChild(fragment)

        return root.querySelector(`.js-calendar-column[data-lecturer-id="${lecturerId}"]`)
    }

    /**
     * プレビュー用に追加した列のうち、ブロックが無くなったものを取り除く
     *
     * @returns {void}
     */
    const removeEmptyPreviewColumns = () => {
        root.querySelectorAll('.js-calendar-column[data-preview-column="1"]').forEach((column) => {
            if (!column.querySelector('.js-calendar-block')) {
                column.remove()
            }
        })
    }

    /**
     * 授業 × 役割のプレビューを取り除く
     *
     * @param {string} lessonId
     * @param {string} role
     * @returns {void}
     */
    const removePreview = (lessonId, role) => {
        root.querySelectorAll(`.js-calendar-preview[data-lesson-id="${lessonId}"][data-role="${role}"]`)
            .forEach((block) => block.remove())
        removeEmptyPreviewColumns()
    }

    /**
     * 未決定列のブロックの見た目を、メイン講師の割り当て予定の有無で切り替える
     *
     * @param {string} lessonId
     * @param {boolean} hasMainPreview
     * @returns {void}
     */
    const markUndecidedBlock = (lessonId, hasMainPreview) => {
        root.querySelectorAll(`.js-calendar-block[data-lesson-id="${lessonId}"][data-role="undecided"]`).forEach((block) => {
            block.style.opacity = hasMainPreview ? '0.4' : ''
            const badge = block.querySelector('.js-calendar-assigned-badge')
            if (badge) {
                badge.hidden = !hasMainPreview
            }
        })
    }

    /**
     * プレビューブロックを組み立てる（textContent で値を入れ、HTML として解釈させない）
     *
     * @param {DOMStringMap} data 行の data-preview-*
     * @param {string} role
     * @param {boolean} overlapping
     * @returns {HTMLElement}
     */
    const buildPreviewBlock = (data, role, overlapping) => {
        const block = document.createElement('div')
        block.className = [
            'js-calendar-block js-calendar-preview absolute rounded border border-dashed overflow-hidden leading-tight px-1',
            roleClasses[role] || '',
            overlapping ? 'border-2 border-red-500' : '',
        ].join(' ')
        block.style.top = `${data.previewTop}px`
        block.style.height = `${data.previewHeight}px`
        block.style.left = '2px'
        block.style.right = '2px'
        block.style.opacity = '0.9'
        block.title = `${data.previewTime} ${data.previewTitle} ${data.previewDetail}（割り当て予定${overlapping ? '・時間帯が重複' : ''}）`

        const head = document.createElement('div')
        head.className = 'font-semibold whitespace-nowrap'
        head.textContent = `${data.previewTime} [${ROLE_LABELS[role]}・予定]`
        if (overlapping) {
            const overlap = document.createElement('span')
            overlap.className = 'text-red-600 font-bold'
            overlap.textContent = ' 重複'
            head.appendChild(overlap)
        }

        const body = document.createElement('div')
        body.className = 'truncate'
        body.textContent = `${data.previewTitle} ${data.previewDetail}`

        block.appendChild(head)
        block.appendChild(body)

        return block
    }

    /**
     * セレクトの現在値に合わせてプレビューを描き直す
     *
     * @param {HTMLSelectElement} select
     * @returns {void}
     */
    const renderPreview = (select) => {
        const matched = SELECT_NAME_PATTERN.exec(select.name)
        if (!matched) {
            return
        }
        const lessonId = matched[1]
        const role = ROLE_BY_INPUT_KEY[matched[2]]
        if (!role) {
            return
        }

        removePreview(lessonId, role)

        const row = select.closest('tr')
        if (!row || !select.value) {
            if (role === 'main') {
                markUndecidedBlock(lessonId, false)
            }
            return
        }

        const option = select.options[select.selectedIndex]
        const column = findOrCreateColumn(select.value, extractLecturerName(option.textContent))
        const body = column.querySelector('.js-calendar-column-body')
        const start = Number(row.dataset.previewStart)
        const end = Number(row.dataset.previewEnd)
        const overlapping = isOverlapping(body, start, end, null)

        const block = buildPreviewBlock(row.dataset, role, overlapping)
        block.dataset.lessonId = lessonId
        block.dataset.role = role
        block.dataset.startMinutes = String(start)
        block.dataset.endMinutes = String(end)
        body.appendChild(block)

        if (role === 'main') {
            markUndecidedBlock(lessonId, true)
        }
    }

    panel.querySelectorAll('select').forEach((select) => {
        select.addEventListener('change', () => renderPreview(select))
        // バリデーションエラーで戻ったときなど、初期値がある場合もプレビューする
        renderPreview(select)
    })
}

document.addEventListener('DOMContentLoaded', setUpCalendarPreview)
